import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { PencilFill, CheckLg, XLg, PlusCircle, Trash } from 'react-bootstrap-icons';
import { useAuth } from '../../contexts/SecurityContext';
import RoleGuard from '../security/RoleGuard';
import SectionControls from '../SectionControls';
import { updateStatCardsSection } from '../../services/contentService';
import './StatCardsBlock.scss';

function defaultStat() {
  return { value: '$1B', label: 'Stat description in uppercase' };
}

export function defaultStatCardsData() {
  return {
    title: 'Section title',
    intro: '',
    stats: [defaultStat(), defaultStat(), defaultStat()],
    footerText: '',
    footerLinkUrl: '',
  };
}

export function StatCardsView({ data }) {
  const stats = Array.isArray(data?.stats) ? data.stats : [];
  // pick column width so 1/2/3+ stats lay out reasonably
  const colSpan = stats.length === 1 ? 12 : stats.length === 2 ? 6 : 4;
  return (
    <div className="stat-cards-block">
      {data?.title ? <h2 className="stat-cards-block__title">{data.title}</h2> : null}
      {data?.intro ? <p className="stat-cards-block__intro">{data.intro}</p> : null}
      <Row className="stat-cards-block__stats g-3">
        {stats.map((stat, idx) => (
          <Col key={idx} md={colSpan} className="stat-cards-block__stat-col">
            <div className="stat-cards-block__stat">
              <div className="stat-cards-block__value">{stat.value}</div>
              <div className="stat-cards-block__label">{stat.label}</div>
            </div>
          </Col>
        ))}
      </Row>
      {data?.footerText ? (
        <p className="stat-cards-block__footer">
          {data.footerLinkUrl ? (
            <a href={data.footerLinkUrl} target="_blank" rel="noopener noreferrer">
              {data.footerText}
            </a>
          ) : (
            data.footerText
          )}
        </p>
      ) : null}
    </div>
  );
}

function StatCardsBlock({
  pageId, section, onMoveUp, onMoveDown, onDelete, isFirstSection, isLastSection,
}) {
  const { user } = useAuth();
  const initialData = useMemo(() => section?.data || defaultStatCardsData(), [section]);
  const [data, setData] = useState(initialData);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setData(section?.data || defaultStatCardsData());
  }, [section]);

  async function handleSave() {
    setIsSaving(true);
    try {
      const modifiedBy = user?.attributes?.email || user?.username || 'user';
      await updateStatCardsSection(pageId, section.position, data, modifiedBy);
      setIsEditing(false);
    } catch (e) {
      console.error('Error saving stat cards:', e);
      // eslint-disable-next-line no-alert
      alert('Failed to save section. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  function handleCancel() {
    setData(section?.data || defaultStatCardsData());
    setIsEditing(false);
  }

  function updateField(field, value) {
    setData((prev) => ({ ...prev, [field]: value }));
  }

  function updateStat(idx, patch) {
    setData((prev) => {
      const next = { ...prev, stats: [...(prev.stats || [])] };
      next.stats[idx] = { ...next.stats[idx], ...patch };
      return next;
    });
  }

  function addStat() {
    setData((prev) => ({ ...prev, stats: [...(prev.stats || []), defaultStat()] }));
  }

  function removeStat(idx) {
    setData((prev) => {
      const next = { ...prev, stats: [...(prev.stats || [])] };
      next.stats.splice(idx, 1);
      return next;
    });
  }

  if (isEditing) {
    return (
      <div className="stat-cards-block stat-cards-block--editing">
        <Form.Group className="mb-3">
          <Form.Label>Section title</Form.Label>
          <Form.Control
            type="text"
            value={data.title || ''}
            onChange={(e) => updateField('title', e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Intro paragraph (optional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={data.intro || ''}
            onChange={(e) => updateField('intro', e.target.value)}
          />
        </Form.Group>

        {(data.stats || []).map((stat, idx) => (
          <div key={idx} className="stat-cards-block__editor-stat">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Stat {idx + 1}</strong>
              <Button variant="outline-danger" size="sm" onClick={() => removeStat(idx)}>
                <Trash /> Remove
              </Button>
            </div>
            <Row className="g-2">
              <Col md={4}>
                <Form.Label>Value (e.g. $139B)</Form.Label>
                <Form.Control
                  type="text"
                  value={stat.value || ''}
                  onChange={(e) => updateStat(idx, { value: e.target.value })}
                />
              </Col>
              <Col md={8}>
                <Form.Label>Label (rendered in uppercase)</Form.Label>
                <Form.Control
                  type="text"
                  value={stat.label || ''}
                  onChange={(e) => updateStat(idx, { label: e.target.value })}
                />
              </Col>
            </Row>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addStat} className="mb-3">
          <PlusCircle /> Add stat
        </Button>

        <div className="stat-cards-block__editor-divider" />
        <Row className="g-2 mb-2">
          <Col md={6}>
            <Form.Label>Footer text (optional)</Form.Label>
            <Form.Control
              type="text"
              placeholder="More information available at: …"
              value={data.footerText || ''}
              onChange={(e) => updateField('footerText', e.target.value)}
            />
          </Col>
          <Col md={6}>
            <Form.Label>Footer link URL (optional, opens new tab)</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://…"
              value={data.footerLinkUrl || ''}
              onChange={(e) => updateField('footerLinkUrl', e.target.value)}
            />
          </Col>
        </Row>

        <div className="stat-cards-block__editor-actions">
          <Button variant="success" onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</>
            ) : (
              <><CheckLg /> Save</>
            )}
          </Button>
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
            <XLg /> Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="stat-cards-block stat-cards-block--view">
      <StatCardsView data={data} />
      <RoleGuard role="admin">
        <div className="admin-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            className="edit-btn"
            onClick={() => setIsEditing(true)}
          >
            <PencilFill /> Edit stats
          </Button>
          <SectionControls
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onDelete={onDelete}
            isFirstSection={isFirstSection}
            isLastSection={isLastSection}
          />
        </div>
      </RoleGuard>
    </div>
  );
}

export default StatCardsBlock;
